import { db } from '@/mocks/db/schema';
import type {
  AiMockSide,
  IndividualPortalAiChatSession,
  IndividualPortalSequenceDefinition,
  IndividualPortalSequenceInstance,
  IndividualPortalSequenceRightPanelMode,
  IndividualPortalSequenceStepRecord,
  IndividualPortalTask,
  IndividualPortalUpcomingInteraction,
  SequenceStepRecordAnswer,
} from '@/mocks/db/schema';
import { rowSide } from '@/mocks/db/types';

export interface SequenceRunnerData {
  instance: IndividualPortalSequenceInstance;
  definition: IndividualPortalSequenceDefinition;
  stepRecords: IndividualPortalSequenceStepRecord[];
}

export interface RecordSequenceAnswerInput {
  instanceId: string;
  stepId: string;
  answer: SequenceStepRecordAnswer;
}

export interface RecordSequenceAnswerResult {
  instance: IndividualPortalSequenceInstance;
  stepRecord: IndividualPortalSequenceStepRecord;
  shouldOpenDetail: boolean;
}

export interface UploadSequenceFilesInput {
  instanceId: string;
  stepId: string;
  files: File[];
}

export interface SetRightPanelModeInput {
  instanceId: string;
  mode: IndividualPortalSequenceRightPanelMode;
}

export interface SetSidebarOpenInput {
  instanceId: string;
  open: boolean;
}

export interface CompleteSequenceResult {
  instance: IndividualPortalSequenceInstance;
  task: IndividualPortalTask;
}

function randomMs(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function simulateLatency(min: number, max: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, randomMs(min, max)));
}

async function recomputeTaskCompletedSteps(
  instanceId: string,
  taskId: string,
): Promise<IndividualPortalTask | null> {
  const completedCount = await db.individualPortalSequenceStepRecords
    .where('instanceId')
    .equals(instanceId)
    .and((r) => r.status === 'completed')
    .count();

  const task = await db.individualPortalTasks.get(taskId);
  if (!task) return null;

  const updated: IndividualPortalTask = {
    ...task,
    completedSteps: Math.min(completedCount, task.totalSteps),
  };
  await db.individualPortalTasks.put(updated);
  return updated;
}

export async function getSequenceRunnerData(
  instanceId: string,
): Promise<SequenceRunnerData> {
  const instance = await db.individualPortalSequenceInstances.get(instanceId);
  if (!instance) {
    throw new Error(`Sequence instance not found: ${instanceId}`);
  }

  const definition = await db.individualPortalSequenceDefinitions.get(
    instance.definitionId,
  );
  if (!definition) {
    throw new Error(`Sequence definition not found: ${instance.definitionId}`);
  }

  const stepRecords = await db.individualPortalSequenceStepRecords
    .where('instanceId')
    .equals(instanceId)
    .toArray();

  const orderById = new Map(definition.steps.map((s) => [s.id, s.order]));
  stepRecords.sort(
    (a, b) => (orderById.get(a.stepId) ?? 0) - (orderById.get(b.stepId) ?? 0),
  );

  return { instance, definition, stepRecords };
}

export async function recordSequenceAnswer(
  input: RecordSequenceAnswerInput,
): Promise<RecordSequenceAnswerResult> {
  await simulateLatency(220, 420);

  const result = await db.transaction(
    'rw',
    [
      db.individualPortalSequenceInstances,
      db.individualPortalSequenceStepRecords,
      db.individualPortalSequenceDefinitions,
    ],
    async () => {
      const instance = await db.individualPortalSequenceInstances.get(
        input.instanceId,
      );
      if (!instance) {
        throw new Error(`Sequence instance not found: ${input.instanceId}`);
      }
      const definition = await db.individualPortalSequenceDefinitions.get(
        instance.definitionId,
      );
      if (!definition) {
        throw new Error(
          `Sequence definition not found: ${instance.definitionId}`,
        );
      }
      const recordId = `${input.instanceId}:${input.stepId}`;
      const existing =
        await db.individualPortalSequenceStepRecords.get(recordId);
      if (!existing) {
        throw new Error(`Step record not found: ${recordId}`);
      }

      const now = new Date().toISOString();
      const updatedRecord: IndividualPortalSequenceStepRecord = {
        ...existing,
        status: 'completed',
        completedAt: now,
        answer: input.answer,
      };
      await db.individualPortalSequenceStepRecords.put(updatedRecord);

      const currentIdx = definition.steps.findIndex(
        (s) => s.id === input.stepId,
      );
      const nextStep = definition.steps[currentIdx + 1] ?? null;

      const isFinalAnswer = input.answer.kind === 'complete';
      // automap_review step への遷移時は rightPanelMode を変えない。
      // chat の tool_call アニメーション → automap_link bubble → ユーザーが
      // 「抽出内容を見る」をクリックした段階で初めて "automap" に切替わる。
      const nextRightPanelMode: IndividualPortalSequenceRightPanelMode =
        isFinalAnswer ? 'detail' : instance.rightPanelMode;
      // kind: "complete" は「最終 step の CTA を押して詳細パネルを開いた」段階。
      // ここで status を "completed" にすると buildEffectiveHistory が即座に
      // completionHistory (受理メッセージ) を staged reveal してしまうため、
      // 完了確定は `completeSequence` (詳細パネルの「確定する」押下) まで遅延させる。
      const updatedInstance: IndividualPortalSequenceInstance = {
        ...instance,
        status: nextStep || isFinalAnswer ? 'in_progress' : 'completed',
        currentStepId: nextStep ? nextStep.id : instance.currentStepId,
        rightPanelMode: nextRightPanelMode,
        // Preserve user sidebar preference; the runner UI computes an
        // effective `open` value that forces collapse during detail mode.
        sidebarOpen: instance.sidebarOpen,
        updatedAt: now,
        completedAt: nextStep || isFinalAnswer ? instance.completedAt : now,
      };
      await db.individualPortalSequenceInstances.put(updatedInstance);

      return {
        instance: updatedInstance,
        stepRecord: updatedRecord,
        shouldOpenDetail: isFinalAnswer,
      };
    },
  );

  await recomputeTaskCompletedSteps(input.instanceId, result.instance.taskId);

  return result;
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('FileReader did not return a string.'));
      }
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}

async function buildUploadedFileMetadata(file: File, receivedAt: string) {
  const base = {
    fileName: file.name,
    sizeBytes: file.size,
    mimeType: file.type || 'application/octet-stream',
    receivedAt,
  };
  if (!file.type.startsWith('image/')) {
    return base;
  }
  try {
    const previewDataUrl = await readFileAsDataUrl(file);
    return { ...base, previewDataUrl };
  } catch {
    // Demo-friendly fallback: skip the preview rather than aborting the upload.
    return base;
  }
}

export async function uploadSequenceFiles(
  input: UploadSequenceFilesInput,
): Promise<{ stepRecord: IndividualPortalSequenceStepRecord }> {
  await simulateLatency(800, 1500);

  const recordId = `${input.instanceId}:${input.stepId}`;
  const now = new Date().toISOString();
  const fileMetadata = await Promise.all(
    input.files.map((file) => buildUploadedFileMetadata(file, now)),
  );

  const stepRecord = await db.transaction(
    'rw',
    db.individualPortalSequenceStepRecords,
    async () => {
      const existing =
        await db.individualPortalSequenceStepRecords.get(recordId);
      if (!existing) {
        throw new Error(`Step record not found: ${recordId}`);
      }

      const previousFiles =
        existing.answer && existing.answer.kind === 'upload'
          ? existing.answer.files
          : [];
      const updated: IndividualPortalSequenceStepRecord = {
        ...existing,
        status: 'in_progress',
        answer: {
          kind: 'upload',
          files: [...previousFiles, ...fileMetadata],
          skipped: false,
        },
      };
      await db.individualPortalSequenceStepRecords.put(updated);
      return updated;
    },
  );

  return { stepRecord };
}

export async function setRightPanelMode(
  input: SetRightPanelModeInput,
): Promise<{ instance: IndividualPortalSequenceInstance }> {
  await simulateLatency(120, 220);

  const instance = await db.individualPortalSequenceInstances.get(
    input.instanceId,
  );
  if (!instance) {
    throw new Error(`Sequence instance not found: ${input.instanceId}`);
  }
  const updated: IndividualPortalSequenceInstance = {
    ...instance,
    rightPanelMode: input.mode,
    updatedAt: new Date().toISOString(),
    // Preserve user preference; the runner UI computes an effective open
    // value that forces collapse during detail mode.
    sidebarOpen: instance.sidebarOpen,
  };
  await db.individualPortalSequenceInstances.put(updated);
  return { instance: updated };
}

export async function setSidebarOpen(
  input: SetSidebarOpenInput,
): Promise<{ instance: IndividualPortalSequenceInstance }> {
  const instance = await db.individualPortalSequenceInstances.get(
    input.instanceId,
  );
  if (!instance) {
    throw new Error(`Sequence instance not found: ${input.instanceId}`);
  }
  const updated: IndividualPortalSequenceInstance = {
    ...instance,
    sidebarOpen: input.open,
    updatedAt: new Date().toISOString(),
  };
  await db.individualPortalSequenceInstances.put(updated);
  return { instance: updated };
}

export interface SequenceSidebarData {
  recentSessions: IndividualPortalAiChatSession[];
  upcomingInteractions: IndividualPortalUpcomingInteraction[];
}

export async function getSequenceSidebarData(
  side: AiMockSide = 'individual',
): Promise<SequenceSidebarData> {
  /** v8 以前や seed 漏れで `side` が未設定の行は {@link rowSide} で "individual" とみなす。 */
  const [sessions, upcomingInteractions] = await Promise.all([
    db.individualPortalAiChatSessions.toArray(),
    db.individualPortalUpcomingInteractions.toArray(),
  ]);
  const recentAll = sessions
    .filter((row) => rowSide(row) === side)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const upcoming = upcomingInteractions
    .filter((row) => rowSide(row) === side)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  return {
    recentSessions: recentAll.slice(0, 10),
    upcomingInteractions: upcoming,
  };
}

export async function completeSequence(
  instanceId: string,
): Promise<CompleteSequenceResult> {
  await simulateLatency(600, 900);

  const result = await db.transaction(
    'rw',
    [db.individualPortalSequenceInstances, db.individualPortalTasks],
    async () => {
      const instance =
        await db.individualPortalSequenceInstances.get(instanceId);
      if (!instance) {
        throw new Error(`Sequence instance not found: ${instanceId}`);
      }
      const now = new Date().toISOString();
      const updated: IndividualPortalSequenceInstance = {
        ...instance,
        status: 'completed',
        // 申請完了後は詳細パネルを閉じて chat 全幅に戻す。
        rightPanelMode: 'cards',
        completedAt: now,
        updatedAt: now,
      };
      await db.individualPortalSequenceInstances.put(updated);

      const task = await db.individualPortalTasks.get(instance.taskId);
      if (!task) {
        throw new Error(`Task not found: ${instance.taskId}`);
      }
      const updatedTask: IndividualPortalTask = {
        ...task,
        completedSteps: task.totalSteps,
      };
      await db.individualPortalTasks.put(updatedTask);

      return { instance: updated, task: updatedTask };
    },
  );

  return result;
}
